<template>
    <seismic-layout>
        <main class="system-page">
            <section class="header">
                <h2>3. Define Brace Type and Piping System</h2>
                <p>
                    Configure your main and branch lines below.
                </p>
            </section>
            <section>
                <h3>Define Brace Orientation</h3>
                <p class="note">
                    Select one type of brace
                </p>
                <fieldset>
                    <div class="radio flex">
                        <div>
                            <div class="help">
                                <i class="fa fa-search-plus inline-popup" data-mfp-src="#lateral-help"></i>
                            </div>
                            <input type="radio" id="lateral" name="orientation" v-model="record.orientation" value="lateral">
                            <label for="lateral">
                                <img src="/img/seismic/lateral-brace.jpg" alt="Lateral Brace" />
                                <span>Lateral</span>
                            </label>
                        </div>

                        <div>
                            <div class="help">
                                <i class="fa fa-search-plus inline-popup" data-mfp-src="#longitudinal-help"></i>
                            </div>
                            <input type="radio" id="longitudinal" name="orientation" v-model="record.orientation" value="longitudinal">
                            <label for="longitudinal">
                                <img src="/img/seismic/longitudinal-brace.jpg" alt="Longitudinal Brace" />
                                <span>Longitudinal</span>
                            </label>
                        </div>

                        <div>
                            <div class="help">
                                <i class="fa fa-search-plus inline-popup" data-mfp-src="#lat-long-help"></i>
                            </div>
                            <input type="radio" id="lateral-brace" name="orientation" v-model="record.orientation" value="lateral-longitudinal">

                            <label for="lateral-brace">
                                <img src="/img/seismic/lat-long-brace.jpg" alt="Lateral Brace as a Lateral & Longitudinal Brace" />
                                <span>Lateral Brace as a Lateral &amp; Longitudinal Brace</span>
                            </label>
                        </div>

                        <div>
                            <div class="help">
                                <i class="fa fa-search-plus inline-popup" data-mfp-src="#riser-wall-help"></i>
                            </div>
                            <input type="radio" id="riser-wall" name="orientation" v-model="record.orientation" value="riser-wall">
                            <label for="riser-wall">
                                <img src="/img/seismic/riser-wall-brace.jpg" alt="Riser / 4-Way Brace Assembly - Wall Condition" />
                                <span>Riser / 4-Way Brace Assembly - Wall Condition</span>
                            </label>
                        </div>

                        <div>
                            <div class="help">
                                <i class="fa fa-search-plus inline-popup" data-mfp-src="#riser-corner-help"></i>
                            </div>
                            <input type="radio" id="riser-corner" name="orientation" v-model="record.orientation" value="riser-corner">
                            <label for="riser-corner">
                                <img src="/img/seismic/riser-corner-brace.jpg" alt="Riser / 4-Way Brace Assembly - Corner Condition" />
                                <span>Riser / 4-Way Brace Assembly - Corner Condition</span>
                            </label>
                        </div>
                    </div>
                </fieldset>

                <seismic-branch-line
                    v-for="branch in branches"
                    :uid="branch.uid"
                    :key="branch.uid"
                    >
                </seismic-branch-line>

                <div>
                    <button @click="addBranch">Add A Branch Line</button>
                </div>

                <div class="totals">
                    <p>
                        Total ZOI System Weight:
                        <span v-if="totalZoiSystemWeight" v-text="lbOrKg(totalZoiSystemWeight, true)"></span>
                        <span v-else>--</span>
                    </p>
                    <p>
                        NFPA System Multiplier: <span v-text="nfpaSystemMultiplier"></span>
                    </p>
                    <p>
                        ZOI System Design Weight, Wp:
                        <span v-if="wp" v-text="lbOrKg(wp, true, true)"></span>
                        <span v-else>--</span>
                    </p>
                    <p>
                        ZOI Seismic Design Load, Fpw = Cp*Wp:
                        <span v-if="cpAndWp" v-text="lbOrKg(cpAndWp, true, true)"></span>
                        <span v-else>--</span>
                    </p>
                </div>

                <div v-html="maxLoadError">

                </div>
            </section>

            <section>
                <seismic-actions
                    @save="validateThenSave"
                    @next="saveThenNext"
                    :fadeSave="!hasChanges"
                    :fadeNext="!cpAndWp || hasRiserNippleErrors"
                    :save-button-finished-text="saveButtonFinishedText"
                    :saving="saving"></seismic-actions>
            </section>
        </main>
        <aside class="primary">
            <seismic-sidebar :design="this.seismicDesignRecord" :brace="this"></seismic-sidebar>
        </aside>
        <div id="lateral-help" class="inline-modal mfp-hide">
            <img src="/img/seismic/lateral-brace.jpg" alt="Lateral Brace" />
        </div>
        <div id="longitudinal-help" class="inline-modal mfp-hide">
            <img src="/img/seismic/longitudinal-brace.jpg" alt="Longitudinal Brace" />
        </div>
        <div id="lat-long-help" class="inline-modal mfp-hide">
            <img src="/img/seismic/lat-long-brace.jpg" alt="Lateral Brace as a Lateral & Longitudinal Brace" />
        </div>
        <div id="riser-wall-help" class="inline-modal mfp-hide">
            <img src="/img/seismic/riser-wall-brace.jpg" alt="Riser / 4-Way Brace Assembly - Wall Condition" />
        </div>
        <div id="riser-corner-help" class="inline-modal mfp-hide">
            <img src="/img/seismic/riser-corner-brace.jpg" alt="Riser / 4-Way Brace Assembly - Corner Condition" />
        </div>
    </seismic-layout>
</template>
<script>
import ClockvineMixin from 'vue/common/clockvine-mixin';
import SeismicDesignLoader from './mixins/seismic-design-loader';

import SeismicLayout from './layout';
import SeismicHeader from './header';
import SeismicSidebar from './shared/sidebar';
import SeismicBranchLine from './shared/branch-line';
import SeismicErrors from './shared/errors';
import SeismicActions from './shared/actions';

import { mapGetters, mapState } from 'vuex';
import ShortUniqueId from 'short-unique-id';
import axios from 'axios';

export default {
    components: {
        SeismicLayout,
        SeismicHeader,
        SeismicSidebar,
        SeismicBranchLine,
        SeismicErrors,
        SeismicActions
    },

    data() {
        return {
            type: 'seismicBraceConfiguration',

            record: {
                orientation: 'lateral',
            },

            saveButtonFinishedText: 'SAVED!'
        };
    },

    watch: {
        ['record.orientation']() {
            this.$store.dispatch('seismicSystem/setOrientation', this.record.orientation);
        },
        cp() {
            this.$store.dispatch('seismicSystem/setCp', this.record.cp);
        },

    },

    mounted() {
        this.$store.dispatch('seismicSystem/fetchAll', { id: this.id });

        var $popup = $('.inline-popup');
        if ($popup.length) {
            $popup.magnificPopup({
                type:'inline'
            });
        }
    },

    computed: {
        cp() {
            // this method is for the sidebar component
            return this.record.cp;
        },

        name() {
            return this.record.name;
        },

        ...mapGetters({
            'compactBranches': 'seismicSystem/compactBranches',
            'hasRiserNippleErrors': 'seismicSystem/hasRiserNippleErrors'
        }),

        ...mapState({
            'nfpaSystemMultiplier': state => state.seismicSystem.nfpaSystemMultiplier,
            'branches': state => state.seismicSystem.branches,
            'totalZoiSystemWeight': state => state.seismicSystem.totalZoiSystemWeight,
            'wp': state => state.seismicSystem.zoiSystemDesignWeightWp,
            'cpAndWp':state => state.seismicSystem.zoiSeismicDesignLoad,
            'systemErrors': state => state.seismicSystem.errors
        }),

        maxLoadError() {
            if (this.systemErrors.maxLoad) {
                return "<p class='messaging error'>Maximum Load (Fpw) in Zone of Influence exceeds the allowable lateral load in the Main</p>";
            }
            return '';
        }
    },

    mixins: [
        ClockvineMixin,
        SeismicDesignLoader,
    ],

    methods: {
        addBranch() {
            this.$store.dispatch('seismicSystem/addBranch');
        },

        isDirty() {
            // always assume data is dirty
            return true;
        },

        getMaxLoadError() {
            this.$store.dispatch('seismicSystem/fetchMaxLoadError');
        },

        validateThenSave() {
            this.saving = true;

            return this.$store.dispatch('seismicSystem/fetchMaxLoadError')
                .then(() => {
                    if (!this.systemErrors.maxLoad) {
                        return this.save();
                    }

                    this.saving = false;
                    return Promise.reject();
                })
                .catch(() => {
                    this.saving = false
                    return Promise.reject();
                });
        },

        saveThenNext() {
            if (!this.cpAndWp || this.hasRiserNippleErrors) {
                return;
            }

            this.validateThenSave().then(() => {
                this.$router.push({name: 'seismic-brace-attachment', params: {
                    id: this.record.id || this.$route.params.id,
                    seismicDesignId: this.seismicDesignId
                }});
            });
        },

        getUrlParams() {
            return { seismicDesignId: this.$route.params.seismicDesignId };
        },

        getRecordParams() {
            let record = Object.assign({}, this.record, {
                totalZoiSystemWeight: this.totalZoiSystemWeight,
                nfpaSystemMultiplier: this.nfpaSystemMultiplier,
                zoiSystemDesignWeight: this.wp,
                zoiSystemDesignLoad: this.cpAndWp,
                branches: this.compactBranches,
            });

            return Object.assign({}, this.getUrlParams(), { data: record });
        },
    },
}
</script>
